import { ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useTeam } from '@/contexts/TeamContext'
import LoadingScreen from '@/components/shared/LoadingScreen'
import { Button } from '@/components/ui/button'
import { useMinimumSplash } from '@/hooks/useMinimumSplash'

interface TeamGuardProps {
  children: ReactNode
}

/** The guard's full-screen answer when there is no team to render. */
function FullScreenMessage({
  title,
  message,
  destructive = false,
  action,
}: {
  title: string
  message: ReactNode
  destructive?: boolean
  action?: { label: string; onClick: () => void }
}): React.JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="mb-2 font-serif text-xl font-medium">{title}</h1>
        <p className={destructive ? 'text-destructive' : 'text-muted-foreground'}>{message}</p>
        {action !== undefined && (
          <Button className="mt-6" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </div>
    </div>
  )
}

export function TeamGuard({ children }: TeamGuardProps): React.JSX.Element {
  const { activeTeam, teams, vanityOrg, vanityDenied, vanityError, email } = useTeam()
  const { logout } = useAuth()
  const splashDone = useMinimumSplash()

  // Vanity team resolution needs /me; if it died after retries, show the
  // error instead of an eternal "Loading team..." spinner (TRA-829).
  if (vanityError !== null) {
    return (
      <FullScreenMessage
        title="Failed to load your teams"
        message={vanityError}
        destructive
        action={{
          label: 'Retry',
          onClick: () => {
            window.location.reload()
          },
        }}
      />
    )
  }

  // Vanity subdomain, authenticated, but not a member of that org (TRA-829).
  // Checked before the no-team case: a teamless user on mercor.trainloop.ai
  // is denied access to mercor, not told to create a team.
  if (vanityDenied) {
    return (
      <FullScreenMessage
        title="Access denied"
        message={
          <>
            {email ?? 'Your account'} is not a member of &ldquo;{vanityOrg}&rdquo;.
          </>
        }
        action={{ label: 'Log in with a different account', onClick: logout }}
      />
    )
  }

  if (!teams.length) {
    return <FullScreenMessage title="No Team" message="You don't belong to any team yet." />
  }

  if (!activeTeam) {
    return <LoadingScreen label="Loading team..." />
  }

  // Team is ready, but hold the splash so the animation is visible instead of
  // flashing for a few frames on fast loads.
  if (!splashDone) {
    return <LoadingScreen />
  }

  return <>{children}</>
}
